import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import Stack from '@mui/material/Stack';
import { IndentedSections } from '@openx/components-core/lib/IndentedSection/IndentedSections';
import type { Option } from '@openx/types/options';
import { useCallback, useMemo, useState } from 'react';
import type {
  FilterCriteria,
  MultiSelectWithRadioConfigurableFilter,
  MultiSelectWithRadioOptionType,
} from '../../types';
import { getInitialMultiSelectWithRadioValues } from '../../utils/multiSelectWithRadio/getInitialMultiSelectWithRadioValues';
import { getSelectedOptions } from '../../utils/typeGuards';
import { AutocompleteSelector } from './AutocompleteSelector';

export type MultiSelectWithRadioFilterProps<Criteria extends FilterCriteria> = {
  options: Option[];
  appliedFilters?: FilterCriteria;
  onOptionsChange: (key: string, value?: string[]) => void;
  config: MultiSelectWithRadioConfigurableFilter<Criteria>['config'];
  filterKey?: string;
};

const multiSelectWithRadioDataTest = (filterKey: string | undefined, optionId: string, suffix: string) => {
  const base = filterKey
    ? `multi-select-with-radio-filter-${filterKey}-${optionId}`
    : `multi-select-with-radio-filter-${optionId}`;
  return suffix ? `${base}-${suffix}` : base;
};

export const MultiSelectWithRadioFilter = <Criteria extends FilterCriteria>({
  options,
  appliedFilters,
  onOptionsChange,
  config,
  filterKey,
}: MultiSelectWithRadioFilterProps<Criteria>) => {
  const [checkedOption, setCheckedOption] = useState(getInitialMultiSelectWithRadioValues(options, appliedFilters));

  const filteredAvailableOptions = useMemo(() => {
    if (!config.removedAvailableOptions?.length) {
      return null;
    }

    const removedMap = config.removedAvailableOptions.reduce<Record<string, Set<string>>>(
      (acc, { optionId, removedOptionId }) => {
        if (!acc[optionId]) {
          acc[optionId] = new Set();
        }

        acc[optionId]!.add(removedOptionId);
        return acc;
      },
      {}
    );

    return Object.entries(removedMap).reduce<
      Record<string, { type: MultiSelectWithRadioOptionType; options: Option[] }>
    >((acc, [optionId, removedSet]) => {
      acc[optionId] = {
        type: config.availableOptions[optionId].type,
        options: config.availableOptions[optionId].options.filter(
          availableOption => !removedSet.has(availableOption.id)
        ),
      };
      return acc;
    }, {});
  }, [config, config.removedAvailableOptions]);

  const handleRadioChange = useCallback(
    (name: string, checked: boolean) => {
      onOptionsChange(name, undefined);
      setCheckedOption({ [name]: checked });
    },
    [onOptionsChange]
  );

  const availableOptions = useMemo(
    () => filteredAvailableOptions ?? config.availableOptions,
    [filteredAvailableOptions, config.availableOptions]
  );

  return (
    <>
      {options.map(option => (
        <Box key={option.id}>
          <FormControlLabel
            value={option.id}
            onChange={(_e, checked) => handleRadioChange(option.id, checked)}
            control={<Radio color="primary" data-test={multiSelectWithRadioDataTest(filterKey, option.id, '')} />}
            label={option.name}
            checked={checkedOption[option.id] || false}
          />

          {checkedOption[option.id] && (
            <Stack
              direction="row"
              sx={{
                mb: 1,
                width: '100%',
              }}
            >
              <IndentedSections
                dataTest={multiSelectWithRadioDataTest(filterKey, option.id, 'subfilter')}
                sx={{ width: '100%' }}
              >
                {availableOptions?.[option.id]?.type === 'select' ? (
                  <AutocompleteSelector
                    options={availableOptions?.[option.id]?.options}
                    selectedOptions={getSelectedOptions(appliedFilters?.[option.id])}
                    onOptionChange={value => onOptionsChange(option.id, value)}
                    inputDataTest={multiSelectWithRadioDataTest(filterKey, option.id, 'subfilter-input')}
                  />
                ) : null}
              </IndentedSections>
            </Stack>
          )}
        </Box>
      ))}
    </>
  );
};
